'use client'

import { Dialog, Text } from '@vinted/web-ui'

import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import { FaqEntryType } from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  blockUser,
  unblockUser,
} from '@marketplace-web/conversation-safety/conversation-safety-data'
import { useSession } from '@marketplace-web/shared/session-data'

type Props = {
  username: string
  userId: number
  isUserBlocked: boolean
  isVisible: boolean
  onCancel: () => void
  onConfirm: () => void
}

const UserBlockModal = ({
  userId,
  username,
  isUserBlocked,
  isVisible,
  onCancel,
  onConfirm,
}: Props) => {
  const translationKey = isUserBlocked ? 'unblock_modal' : 'block_modal'
  const translate = useTranslate(`user.${translationKey}`)
  const currentUserId = useSession().user?.id

  const getLearnMoreLink = () => (
    <HelpCenterFaqEntryUrl
      key="leran-more-link"
      type={FaqEntryType.BlockingMember}
      render={url => (
        <a href={url} target="_blank" rel="noreferrer">
          {translate('learn_more_link')}
        </a>
      )}
    />
  )

  const handleToggleBlockClick = async () => {
    if (!currentUserId) return

    const toggleFunction = isUserBlocked ? unblockUser : blockUser
    await toggleFunction({ currentUserId, blockedUserId: userId })

    onConfirm()
  }

  return (
    <Dialog
      show={isVisible}
      title={translate('title', { username })}
      body={
        <Text as="span" text={translate('body', { learn_more_link: getLearnMoreLink() })} html />
      }
      testId="user-block-modal"
      actions={[
        {
          text: isUserBlocked ? translate('actions.unblock') : translate('actions.block'),
          callback: handleToggleBlockClick,
          style: 'filled',
          theme: isUserBlocked ? 'primary' : 'warning',
          testId: `user-block-modal--${isUserBlocked ? 'unblock' : 'block'}`,
        },
        {
          text: translate('actions.cancel'),
          callback: onCancel,
          theme: isUserBlocked ? 'primary' : 'warning',
          style: 'flat',
          testId: 'user-block-modal--cancel',
        },
      ]}
    />
  )
}

export default UserBlockModal
